import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * Custom loading skeleton for HomeTab cards
 * Matches the lavender/purple theme for better visual consistency
 */
export const HomeTabSkeleton = () => {
  return (
    <div className="space-y-3 sm:space-y-5 animate-fade-in">
      {/* Energy Level & Flare Predictor Row */}
      <div className="grid md:grid-cols-2 gap-3 sm:gap-5">
        {/* Energy Card Skeleton */}
        <Card className="overflow-hidden relative rounded-2xl bg-gradient-to-br from-primary/5 to-primary/3">
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-center gap-4 sm:gap-6">
              <Skeleton className="h-24 w-24 rounded-full bg-primary/10" />
              <div className="flex-1 space-y-3">
                <Skeleton className="h-5 w-32 bg-primary/10" />
                <Skeleton className="h-4 w-48 bg-primary/10" />
                <Skeleton className="h-3 w-40 bg-primary/10" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Flare Predictor Skeleton */}
        <Card className="overflow-hidden relative rounded-2xl bg-gradient-to-br from-primary/5 to-primary/3">
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-center gap-4">
              <Skeleton className="h-16 w-16 rounded-full bg-primary/10" />
              <div className="flex-1 space-y-3">
                <Skeleton className="h-5 w-40 bg-primary/10" />
                <Skeleton className="h-4 w-full bg-primary/10" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pain Tracker Row */}
      <div className="grid md:grid-cols-2 gap-3 sm:gap-5">
        <Card className="rounded-2xl bg-gradient-to-br from-primary/5 to-primary/3">
          <CardContent className="p-4 sm:p-6">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Skeleton className="h-10 w-10 rounded-lg bg-primary/10" />
                <Skeleton className="h-6 w-32 bg-primary/10" />
              </div>
              <div className="space-y-2">
                <Skeleton className="h-4 w-full bg-primary/10" />
                <Skeleton className="h-4 w-3/4 bg-primary/10" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl bg-gradient-to-br from-primary/5 to-primary/3">
          <CardContent className="p-4 sm:p-6">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Skeleton className="h-10 w-10 rounded-lg bg-primary/10" />
                <Skeleton className="h-6 w-32 bg-primary/10" />
              </div>
              <div className="space-y-2">
                <Skeleton className="h-4 w-full bg-primary/10" />
                <Skeleton className="h-4 w-3/4 bg-primary/10" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top Symptoms Skeleton - NO backdrop-blur to prevent navigation blur */}
      <Card className="rounded-2xl bg-gradient-to-br from-primary/12 via-primary/6 to-primary/8 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute -right-20 -top-20 w-48 h-48 bg-primary/8 rounded-full blur-3xl" />
          <div className="absolute -left-20 -bottom-20 w-48 h-48 bg-primary/6 rounded-full blur-3xl" />
        </div>
        <CardContent className="p-4 sm:p-6 relative z-10 bg-card/98">
          <div className="flex items-center gap-3 mb-4">
            <Skeleton className="h-12 w-12 rounded-xl bg-primary/10" />
            <Skeleton className="h-6 w-40 bg-primary/10" />
          </div>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-card/50">
                <Skeleton className="h-6 w-6 rounded-full bg-primary/10" />
                <Skeleton className="h-4 flex-1 bg-primary/10" />
                <Skeleton className="h-4 w-16 bg-primary/10" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Chart Skeleton */}
      <Card className="rounded-2xl bg-gradient-to-br from-primary/5 to-primary/3">
        <CardContent className="p-4 sm:p-6">
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Skeleton className="h-10 w-10 rounded-lg bg-primary/10" />
              <Skeleton className="h-6 w-48 bg-primary/10" />
            </div>
            <Skeleton className="h-64 w-full bg-primary/10 rounded-lg" />
          </div>
        </CardContent>
      </Card>

      {/* Appointments & Streak Row */}
      <div className="grid md:grid-cols-2 gap-3 sm:gap-5">
        <Card className="rounded-2xl bg-gradient-to-br from-primary/5 to-primary/3">
          <CardContent className="p-4 sm:p-6">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Skeleton className="h-10 w-10 rounded-lg bg-primary/10" />
                <Skeleton className="h-6 w-40 bg-primary/10" />
              </div>
              <div className="space-y-3">
                <Skeleton className="h-4 w-full bg-primary/10" />
                <Skeleton className="h-4 w-2/3 bg-primary/10" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl bg-gradient-to-br from-primary/5 to-primary/3">
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-center gap-4">
              <Skeleton className="h-16 w-16 rounded-2xl bg-primary/10" />
              <div className="flex-1 space-y-3">
                <Skeleton className="h-5 w-32 bg-primary/10" />
                <Skeleton className="h-4 w-48 bg-primary/10" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pulsing effect overlay */}
      <style>{`
        @keyframes skeleton-shimmer {
          0% {
            opacity: 0.6;
          }
          50% {
            opacity: 1;
          }
          100% {
            opacity: 0.6;
          }
        }
        .animate-fade-in {
          animation: skeleton-shimmer 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};
